/**
 * useAdminSettings — Hook für Admin-Einstellungen
 * Verwaltet: Zugangsdaten, Marken-Konfiguration, KI-Anweisungen
 * 
 * SICHERHEIT: Alle Daten sind über RLS nur für Admin-Rolle zugänglich.
 * Die Tabellen admin_zugangsdaten und marken_config haben RLS-Policies
 * die nur Benutzer mit rolle='admin' in der mitarbeiter-Tabelle zulassen.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Zugangsdaten {
  id: string;
  kategorie: string;
  dienst: string;
  schluessel: string;
  wert: string;
  beschreibung: string | null;
  ist_geheim: boolean;
  erstellt_am: string;
  aktualisiert_am: string;
}

export interface MarkenConfig {
  id: string;
  marke: string;
  anzeigename: string;
  beschreibung: string | null;
  logo_url: string | null;
  farbe_primaer: string | null;
  farbe_sekundaer: string | null;
  domain: string | null;
  ki_anweisungen: string | null;
  design_richtlinien: string | null;
  ist_aktiv: boolean;
  erstellt_am: string;
  aktualisiert_am: string;
}

// ─── useZugangsdaten ────────────────────────────────────────────────────────

export function useZugangsdaten() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState<Zugangsdaten[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!isAdmin) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: result, error: err } = await supabase
        .from('admin_zugangsdaten')
        .select('*')
        .order('kategorie')
        .order('dienst');
      if (err) throw err;
      setData(result || []);
      setError(null);
    } catch (e: any) {
      setError(e.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (item: Omit<Zugangsdaten, 'id' | 'erstellt_am' | 'aktualisiert_am'>) => {
    const { error: err } = await supabase
      .from('admin_zugangsdaten')
      .insert(item);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Zugangsdaten gespeichert');
    fetch();
    return true;
  }, [fetch]);

  const update = useCallback(async (id: string, updates: Partial<Zugangsdaten>) => {
    const { error: err } = await supabase
      .from('admin_zugangsdaten')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Zugangsdaten aktualisiert');
    fetch();
    return true;
  }, [fetch]);

  const remove = useCallback(async (id: string) => {
    const { error: err } = await supabase
      .from('admin_zugangsdaten')
      .delete()
      .eq('id', id);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Zugangsdaten gelöscht');
    fetch();
    return true;
  }, [fetch]);

  return { data, loading, error, refetch: fetch, create, update, remove };
}

// ─── useMarkenConfig ────────────────────────────────────────────────────────

export function useMarkenConfig() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState<MarkenConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!isAdmin) {
      setData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: result, error: err } = await supabase
        .from('marken_config')
        .select('*')
        .order('anzeigename');
      if (err) throw err;
      setData(result || []);
      setError(null);
    } catch (e: any) {
      setError(e.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetch(); }, [fetch]);

  const update = useCallback(async (id: string, updates: Partial<MarkenConfig>) => {
    const { error: err } = await supabase
      .from('marken_config')
      .update({ ...updates, aktualisiert_am: new Date().toISOString() })
      .eq('id', id);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Marken-Konfiguration gespeichert');
    fetch();
    return true;
  }, [fetch]);

  const create = useCallback(async (item: Omit<MarkenConfig, 'id' | 'erstellt_am' | 'aktualisiert_am'>) => {
    const { error: err } = await supabase
      .from('marken_config')
      .insert(item);
    if (err) { toast.error(`Fehler: ${err.message}`); return false; }
    toast.success('Marke angelegt');
    fetch();
    return true;
  }, [fetch]);

  return { data, loading, error, refetch: fetch, update, create };
}
